import * as Sentry from '@sentry/browser'
import { fetchQuery } from 'react-relay'
import { useRelayEnvironment } from 'react-relay/hooks'
import { Variables } from 'react-relay-network-modern'
import {
  createOperationDescriptor,
  Environment,
  getRequest,
  GraphQLTaggedNode,
} from 'relay-runtime'

const prefetchQuery = async (
  environment: Environment,
  query: GraphQLTaggedNode,
  variables: Variables,
) => {
  const operation = createOperationDescriptor(getRequest(query), variables)
  const operationStatus = environment.check(operation).status

  // If the query is already available in the store, we don't need to fetch it
  if (operationStatus === 'available') {
    return
  }

  try {
    const data = await fetchQuery(environment, query, variables).toPromise()
    environment.retain(operation)
    return data
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag(
        'portal-prefetchQuery',
        'An error occurred while prefetching a query',
      )
      Sentry.captureException(error)
    })
  }
}

const prefetchQueries = async (
  environment: Environment,
  queries: GraphQLTaggedNode[],
  variables: Variables,
) => {
  const staleOrMissingOperations = queries
    .map((query) => ({
      query,
      descriptor: createOperationDescriptor(getRequest(query), variables),
    }))
    .filter(
      ({ descriptor }) => environment.check(descriptor).status !== 'available',
    )

  // If none of the queries are stale or missing, we don't need to fetch them
  if (staleOrMissingOperations.length === 0) {
    return
  }

  try {
    const data = await Promise.all(
      staleOrMissingOperations.map(({ query }) =>
        fetchQuery(environment, query, variables).toPromise(),
      ),
    )
    const operations = staleOrMissingOperations.map(
      ({ descriptor }) => descriptor,
    )
    operations.forEach((operation) => environment.retain(operation))
    return data
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag(
        'portal-prefetchQueries',
        'An error occurred while prefetching a query',
      )
      Sentry.captureException(error)
    })
  }
}

export const usePrefetchQuery = () => {
  const environment = useRelayEnvironment()
  return {
    /**
     * Prefetch a single query. If there is data in the store for the query, it will
     * not fetch it again.
     */
    prefetchQuery: (query: GraphQLTaggedNode, variables: Variables) =>
      prefetchQuery(environment, query, variables),
    /**
     * Prefetch multiple queries at once. This is useful when you have multiple queries that
     * share the same variables. If there is data in the store for any of the queries, it will
     * filter those out and only fetch the queries that are missing or stale.
     */
    prefetchQueries: (queries: GraphQLTaggedNode[], variables: Variables) =>
      prefetchQueries(environment, queries, variables),
  }
}
